import type { AbstractConstructor } from "@infra-blocks/types";
import { z } from "zod";
import { attributeProperty } from "../property.js";
import type { ParserType } from "../schemas/parser";

type HtmlElementCtor = AbstractConstructor<HTMLElement>;

export declare abstract class IFormAssociatedElement {
  static readonly formAssociated: true;

  /**
  Reactive property corresponding to the submitted value's key.
  */
  name: Name;

  protected readonly internals: ElementInternals;
  /*
  Form related properties.
  */
  readonly form: HTMLFormElement | null;
  readonly validity: ValidityState;
  readonly validationMessage: string;
  readonly willValidate: boolean;
  /*
  Form related methods.
  */
  checkValidity(): boolean;
  reportValidity(): boolean;

  // TODO: both these things should be implemented in the subclass.
  formDisabledCallback(disabled: boolean): void;
  formResetCallback(): void;
}

export type Name = ParserType<typeof Name>;
export const Name = attributeProperty("name", z.string());

export const FormAssociationMixin = <T extends HtmlElementCtor>(
  SuperClass: T,
) => {
  abstract class FormAssociatedElement extends SuperClass {
    static readonly formAssociated = true;

    static properties = {
      name: Name,
    };

    declare name: Name;

    protected internals: ElementInternals;

    // biome-ignore lint/suspicious/noExplicitAny: Rest argument of any is mandatory in mixins.
    constructor(...args: any[]) {
      super(...args);
      this.internals = this.attachInternals();
      this.name = undefined;
    }

    get form() {
      return this.internals.form;
    }

    get validity() {
      return this.internals.validity;
    }

    get validationMessage() {
      return this.internals.validationMessage;
    }

    get willValidate() {
      return this.internals.willValidate;
    }

    checkValidity() {
      return this.internals.checkValidity();
    }

    reportValidity() {
      return this.internals.reportValidity();
    }
  }
  return FormAssociatedElement as unknown as T &
    AbstractConstructor<IFormAssociatedElement>;
};
